import { Link, NavLink, useLocation } from "react-router-dom";
import { Home, Car, KeyRound, Plus, User, Search, Bell, Languages, Tag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_carmarket-preview/artifacts/vmguiuiy_logo%20v9%20use-02%282%29.png";

function Brand({ size = "md" }) {
  const sizes = {
    sm: "w-8 h-8",
    md: "w-10 h-10",
    lg: "w-12 h-12",
  };
  return (
    <div className={`${sizes[size]} rounded-xl bg-white border border-slate-200 overflow-hidden flex-shrink-0 shadow-sm`}>
      <img src={LOGO_URL} alt="Car Market" className="w-full h-full object-cover" />
    </div>
  );
}

export function TopBar() {
  const { user } = useAuth();
  const { t, lang, toggle } = useI18n();
  return (
    <header
      data-testid="topbar"
      className="sticky top-0 z-40 glass border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 min-w-0">
          <Brand size="md" />
          <div className="hidden sm:block min-w-0">
            <div className="font-display font-extrabold text-base leading-tight tracking-tight text-cm-primary truncate">
              {t("brand.name")}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cm-accent leading-none mt-0.5 truncate">
              {t("brand.tagline")}
            </div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <NavLink to="/buy" data-testid="nav-buy" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.buy")}</NavLink>
          <NavLink to="/rent" data-testid="nav-rent" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.rent")}</NavLink>
          <NavLink to="/sell" data-testid="nav-sell" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.sell")}</NavLink>
          <NavLink to="/pricing" data-testid="nav-pricing" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.pricing")}</NavLink>
          <NavLink to="/blog" data-testid="nav-blog" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.blog")}</NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin" data-testid="nav-admin" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.admin")}</NavLink>
          )}
          {user?.role === 'dealer' && (
            <NavLink to="/dealer-dashboard" data-testid="nav-dealer-dashboard" className={({isActive}) => `px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-slate-100 text-cm-primary' : 'text-slate-600 hover:text-cm-primary hover:bg-slate-50'}`}>{t("nav.dashboard")}</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <button onClick={toggle} data-testid="lang-toggle" className="px-2.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-cm-primary text-xs font-semibold flex items-center gap-1.5 transition-colors">
            <Languages className="w-3.5 h-3.5" /> <span>{lang === "en" ? "ខ្មែរ" : "EN"}</span>
          </button>
          <Link to="/buy" data-testid="topbar-search-btn" className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center md:hidden">
            <Search className="w-5 h-5 text-slate-700" />
          </Link>
          <button data-testid="topbar-notifications" className="hidden lg:flex w-10 h-10 rounded-full hover:bg-slate-100 items-center justify-center">
            <Bell className="w-5 h-5 text-slate-700" />
          </button>
          {user ? (
            <Link to="/account" data-testid="topbar-account" className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition-colors">
              <div className="w-7 h-7 rounded-full bg-cm-primary text-white flex items-center justify-center text-xs font-semibold">
                {user.full_name?.[0]?.toUpperCase() || 'U'}
              </div>
              <span className="hidden sm:inline text-sm font-medium text-cm-primary">{user.full_name?.split(' ')[0]}</span>
            </Link>
          ) : (
            <Link to="/login" data-testid="topbar-login" className="px-4 py-2 rounded-full bg-cm-primary text-white text-sm font-medium hover:bg-cm-accent transition-colors press-scale">
              {t("nav.sign_in")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

export function BottomNav() {
  const { user } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const items = [
    { to: "/", label: t("nav.home"), icon: Home, testid: "bottom-nav-home" },
    { to: "/buy", label: t("nav.buy"), icon: Car, testid: "bottom-nav-buy" },
    { to: "/sell", label: t("nav.sell"), icon: Plus, testid: "bottom-nav-sell", center: true },
    { to: "/rent", label: t("nav.rent"), icon: KeyRound, testid: "bottom-nav-rent" },
    { to: user ? "/account" : "/login", label: t("nav.account"), icon: User, testid: "bottom-nav-account" },
  ];

  return (
    <nav
      data-testid="bottom-nav"
      className="fixed bottom-0 left-0 right-0 h-16 glass border-t border-slate-200 flex justify-around items-center z-50 md:hidden"
      style={{ boxShadow: '0 -4px 20px rgba(0,0,0,0.05)' }}
    >
      {items.map((it) => {
        const active = location.pathname === it.to;
        const Icon = it.icon;
        if (it.center) {
          return (
            <NavLink key={it.to} to={it.to} data-testid={it.testid} className="flex flex-col items-center justify-center -mt-6">
              <div className="w-14 h-14 rounded-full bg-cm-accent text-white flex items-center justify-center shadow-lg shadow-cm-accent/40 press-scale">
                <Icon className="w-6 h-6" strokeWidth={2.5} />
              </div>
              <span className="text-[10px] mt-1 text-cm-primary font-medium">{it.label}</span>
            </NavLink>
          );
        }
        return (
          <NavLink key={it.to} to={it.to} data-testid={it.testid} className="flex flex-col items-center justify-center gap-0.5 px-3 py-1">
            <Icon className={`w-5 h-5 ${active ? 'text-cm-accent' : 'text-slate-500'}`} strokeWidth={2.2} />
            <span className={`text-[10px] ${active ? 'text-cm-accent font-semibold' : 'text-slate-500'}`}>{it.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

export function Footer() {
  const { t } = useI18n();
  return (
    <footer data-testid="footer" className="hidden md:block border-t border-slate-200 mt-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5 mb-3">
            <Brand size="md" />
            <div>
              <div className="font-display font-extrabold text-lg text-cm-primary">{t("brand.name")}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cm-accent">{t("brand.tagline")}</div>
            </div>
          </div>
          <p className="text-sm text-slate-600 max-w-md">{t("footer.tag")}</p>
        </div>
        <div>
          <h4 className="font-semibold text-cm-primary mb-3">{t("footer.market")}</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/buy" className="hover:text-cm-accent">{t("home.qa.buy")}</Link></li>
            <li><Link to="/rent" className="hover:text-cm-accent">{t("home.qa.rent")}</Link></li>
            <li><Link to="/sell" className="hover:text-cm-accent">{t("home.qa.sell")}</Link></li>
            <li><Link to="/pricing" className="hover:text-cm-accent">{t("nav.pricing")}</Link></li>
            <li><Link to="/blog" className="hover:text-cm-accent">{t("nav.blog")}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold text-cm-primary mb-3">{t("footer.company")}</h4>
          <ul className="space-y-2 text-sm text-slate-600">
            <li><Link to="/login" className="hover:text-cm-accent">{t("nav.sign_in")}</Link></li>
            <li><Link to="/register" className="hover:text-cm-accent">{t("auth.role_dealer")}</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} S.P Car market Co., Ltd. All Rights Reserved.
      </div>
    </footer>
  );
}

export default function AppLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <TopBar />
      <main className="flex-1 pb-20 md:pb-0">{children}</main>
      <Footer />
      <BottomNav />
    </div>
  );
}
